// @ts-check
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Drawer } from 'ui-components/components/drawer/drawer';
import { DrawerHeader } from 'ui-components/components/drawer/drawer-header';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';

import { ManageSubscriptionDrawerFooter } from './footer';
import { messages } from './messages';
import { PlanGridLoader } from './plan-grid-loader';
import { PlanGridTabs } from './plan-grid-tabs';
import { PlanSummary } from './plan-summary';
import styles from './styles.module.less';
import { useManageSubscriptionDrawerSectionVisibility } from './use-manage-subscription-drawer-section-visibility';
import { useManageSubscriptionDrawerContext } from '../../context/manage-subscription-drawer-context';
import { useGetNumberOfActiveUsers } from '../../hooks/use-plan-details';
import { PageEvents, useTrackEvent } from '../../hooks/use-track-event';
import { findPlanReference, getAddOnsToAddAndRemove } from '../../utils/plan-utilities';
import { useBuildMarketplaceAddOns } from '../../../../../hooks/add-ons/use-build-addons';
import Global from '../../../../../store/global-selectors';
import { SubscriptionDrawerTopBanners } from './subscription-drawer-banner/subscription-drawer-banner';
import { getAdditionalAnalyticsLicenseLimitForNewPlan } from './helpers';

// Note: FYL - I find myself preferring to place state management here in the drawer for drawer specific stuff, when possible
// The context Parent of this component is a stopgap, bloated with old and new implementations
/**
 * @param {object} props
 * @param {() => void} props.onCancel - Called when user cancels/closes the drawer
 * @param {() => void} props.onClose - Called when drawer should close
 * @param {() => boolean} [props.onInterceptClose] - Optional callback to intercept close attempts. Return false to block close.
 * @returns {JSX.Element}
 */
export const ManageSubscriptionDrawer = ({ onInterceptClose, onCancel, onClose }) => {
  const planInfo = useSelector(Global.selectSellerPlanInfo);
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);
  const {
    desiredNumberOfAdditionalUsers,
    desiredNumberOfAdditionalAnalyticsLicenses,
    setSelectedPlanId,
    setDesiredNumberOfAdditionalUsers,
    setDesiredNumberOfAdditionalAnalyticsLicenses,
    setAddOnsToAdd,
    setAddOnsToRemove,
  } = useManageSubscriptionDrawerContext();

  const numberOfActiveUsers = useGetNumberOfActiveUsers();
  const { addOns } = useBuildMarketplaceAddOns();

  const { canMigrate, shouldForceNewestPlans } = useManageSubscriptionDrawerSectionVisibility();
  const trackSelectPlan = useTrackEvent(PageEvents.SUBSCRIPTION_CHANGE_SELECT_PLAN);

  // Automatically adjust the number of additional users and addOns to add/remove when the selected plan changes
  const onSelectPlan = useCallback(
    /**
     * @param {string} selectedPlanId
     */
    (selectedPlanId) => {
      setSelectedPlanId(selectedPlanId);
      trackSelectPlan({ selectedPlanId });
      const targetPlan = findPlanReference(selectedPlanId, subscriptionData);
      const { addOnsToAdd, addOnsToRemove } = getAddOnsToAddAndRemove(selectedPlanId, subscriptionData, addOns, planInfo);
      setAddOnsToAdd(addOnsToAdd);
      setAddOnsToRemove(addOnsToRemove);

      const numberOfIncludedSeatsInTargetPlan = targetPlan?.userLimit?.limit ?? 0;
      if (numberOfIncludedSeatsInTargetPlan > numberOfActiveUsers) {
        setDesiredNumberOfAdditionalUsers(0);
      } else {
        const minNumberOfSeatsToCover = Math.max(0, numberOfActiveUsers - numberOfIncludedSeatsInTargetPlan);
        if (desiredNumberOfAdditionalUsers !== minNumberOfSeatsToCover) {
          setDesiredNumberOfAdditionalUsers(minNumberOfSeatsToCover);
        }
      }

      // Auto-adjust analytics licenses when switching plans
      const newAdditional = getAdditionalAnalyticsLicenseLimitForNewPlan(
        targetPlan,
        subscriptionData,
        desiredNumberOfAdditionalAnalyticsLicenses,
      );
      setDesiredNumberOfAdditionalAnalyticsLicenses(newAdditional);
    },
    [
      addOns,
      desiredNumberOfAdditionalAnalyticsLicenses,
      desiredNumberOfAdditionalUsers,
      numberOfActiveUsers,
      planInfo,
      setAddOnsToAdd,
      setAddOnsToRemove,
      setDesiredNumberOfAdditionalAnalyticsLicenses,
      setDesiredNumberOfAdditionalUsers,
      setSelectedPlanId,
      subscriptionData,
      trackSelectPlan,
    ],
  );

  const handleClose = useCallback(() => {
    if (onInterceptClose && !onInterceptClose()) {
      return;
    }
    onClose();
  }, [onInterceptClose, onClose]);

  const handleCancel = useCallback(() => {
    if (onInterceptClose && !onInterceptClose()) {
      return;
    }
    onCancel();
  }, [onInterceptClose, onCancel]);

  return (
    <Drawer
      className={styles['manage-subscription-drawer']}
      confirmCancelBodyMessage={onInterceptClose ? undefined : messages.subscriptionDrawerConfirmExitBody}
      confirmCancelButtonMessage={onInterceptClose ? undefined : messages.subscriptionDrawerConfirmExitButton}
      confirmCancelHeaderMessage={onInterceptClose ? undefined : messages.subscriptionDrawerConfirmExitHeader}
      onCancel={handleCancel}
      onClose={handleClose}
    >
      <DrawerHeader
        closeButtonAriaLabel={messages.closeDrawerButtonAriaLabel}
        title={<FormattedMessage {...messages.drawerHeaderText} />}
      />
      <div className={styles['scroll-container']}>
        <FlexRow className={styles['drawer-body']}>
          <SubscriptionDrawerTopBanners />
          <FlexColumn className={styles['collapsible-section-column']}>
            {canMigrate && !shouldForceNewestPlans ? (
              <PlanGridTabs onSelectPlan={onSelectPlan} />
            ) : (
              <PlanGridLoader onSelectPlan={onSelectPlan} />
            )}
          </FlexColumn>
          <FlexColumn>
            <PlanSummary />
          </FlexColumn>
          <ManageSubscriptionDrawerFooter />
        </FlexRow>
      </div>
    </Drawer>
  );
};
